import { Application } from 'pixi.js';
import { VIEW_H, VIEW_W } from './render/AreaView';
import { KnyttWorld } from './engine/KnyttWorld';
import { KnyttSave } from './engine/KnyttSave';
import { Game } from './game/Game';
import { Menu, type WorldEntry } from './ui/Menu';
import { TouchControls } from './ui/TouchControls';
import { PausePanel } from './ui/PausePanel';
import { Cutscene } from './ui/Cutscene';
import { MapPanel } from './ui/MapPanel';
import { initTheme } from './ui/Theme';
import { resetIcon, setLevelIcon } from './ui/Favicon';

// Track OS light/dark changes while in 'auto' (index.html applied the initial
// resolved theme pre-CSS; Theme.ts owns it from here).
initTheme();

// iOS Safari ignores user-scalable=no for pinch zoom and can still zoom via
// its proprietary gesture events; CSS touch-action handles double-tap.
for (const ev of ['gesturestart', 'gesturechange', 'gestureend']) {
  document.addEventListener(ev, (e) => e.preventDefault(), { passive: false });
}

const cp1252 = new TextDecoder('windows-1252');
const DATA_BASE = `${import.meta.env.BASE_URL}data/`;

const BUNDLED_WORLDS: WorldEntry[] = [
  { file: 'Nifflas - Tutorial.knytt.bin', name: 'Tutorial', author: 'Nifflas' },
  { file: 'Nifflas - An Underwater Adventure.knytt.bin', name: 'An Underwater Adventure', author: 'Nifflas' },
  { file: 'Nifflas - The Machine.knytt.bin', name: 'The Machine', author: 'Nifflas' },
  { file: 'Nifflas - Gustav\'s Daughter.knytt.bin', name: "Gustav's Daughter", author: 'Nifflas' },
  { file: 'Nifflas - Sky Flowerz.knytt.bin', name: 'Sky Flowerz', author: 'Nifflas' },
  { file: 'Nifflas - This Level is Unfinished.knytt.bin', name: 'This Level is Unfinished', author: 'Nifflas' },
  { file: 'Nifflas - Original Tutorial.knytt.bin', name: 'Original Tutorial', author: 'Nifflas' },
  { file: 'Nifflas - Handheld Tutorial.knytt.bin', name: 'Handheld Tutorial', author: 'Nifflas' },
  { file: 'Nifflas - Touch Tutorial.knytt.bin', name: 'Touch Tutorial', author: 'Nifflas' },
];

class KnyttApp {
  app!: Application;
  container: HTMLElement;
  menu!: Menu;
  game: Game | null = null;
  touch: TouchControls | null = null;
  worlds: WorldEntry[] = BUNDLED_WORLDS;
  // Resolves once Pixi's renderer (WebGL context, canvas, resize hook) is ready.
  // The main menu is plain DOM/CSS and doesn't need any of this, so `init()`
  // kicks it off but shows the menu without waiting on it — only starting an
  // actual world awaits it (in `startWorld`).
  private appReady!: Promise<void>;

  constructor() {
    this.container = document.getElementById('app')!;
  }

  async init(): Promise<void> {
    this.app = new Application();
    this.appReady = this.app
      .init({
        backgroundColor: 0x000000,
        antialias: false,
        roundPixels: true,
        preference: 'webgl',
        // Render at 1x logical resolution and let the compositor upscale the
        // canvas to the display's density with image-rendering:pixelated below.
        // CSS px → device px is exactly the (integer) DPR, so each canvas pixel
        // becomes an exact 2x2/3x3 device-pixel block — crisp, and far less GPU
        // fill than rendering at DPR (a big battery/heat win on phones).
        resolution: 1,
        autoDensity: true,
      })
      .then(() => {
        // Cap at 60fps: on 120Hz displays (ProMotion iPhones/iPads, high-refresh
        // desktops) rAF fires at 120/s and would double all sim + render work.
        this.app.ticker.maxFPS = 60;
        this.app.canvas.style.imageRendering = 'pixelated';
        this.container.appendChild(this.app.canvas);
        window.addEventListener('resize', this.resizeCanvas);
        this.resizeCanvas();
      });

    // Enrich the bundled list with icons + metadata extracted into the manifest.
    try {
      const resp = await fetch(`${DATA_BASE}worlds/index.json`);
      if (resp.ok) {
        const manifest = (await resp.json()) as WorldEntry[];
        const byFile = new Map(manifest.map((m) => [m.file, m]));
        this.worlds = BUNDLED_WORLDS.map((w) => {
          const m = byFile.get(w.file);
          return m
            ? { ...w, icon: m.icon, size: m.size, difficulty: m.difficulty, description: m.description }
            : w;
        });
      }
    } catch {
      /* fall back to the hardcoded list */
    }

    this.menu = new Menu(this.container, this.worlds, {
      onSelectBundled: (entry) => this.loadBundled(entry),
      onUpload: (file) => this.loadUploaded(file),
      onTutorial: () => this.continueOrTutorial(),
      hasContinue: () => !!localStorage.getItem('knytt-last-file'),
    });
    this.menu.show();
  }

  // The canvas is only as big as the game picture itself (600x240 aspect,
  // fit to the window, capped at 2x); #app's flexbox centres it. No
  // full-viewport canvas with letterbox bars rendered inside.
  private resizeCanvas = (): void => {
    // The gamepad bar (during gameplay) reserves the bottom strip; the
    // canvas centres in what's left above it.
    const padH = this.touch?.height ?? 0;
    this.container.style.paddingBottom = `${padH}px`;
    const availH = Math.max(1, window.innerHeight - padH);
    const scale = Math.min(2, Math.max(0.1, Math.min(window.innerWidth / VIEW_W, availH / VIEW_H)));
    this.app.renderer.resize(
      Math.min(window.innerWidth, Math.round(VIEW_W * scale)),
      Math.min(availH, Math.round(VIEW_H * scale)),
      1, // backing store stays at 1x logical; the compositor pixelated-upscales to device px
    );
  };

  private continueOrTutorial(): void {
    const lastFile = localStorage.getItem('knytt-last-file');
    // MainMenu.cs:64-85: HTML5 builds start "Nifflas - Original Tutorial";
    // touch devices get the Touch Tutorial (both are bundled).
    const tutorialFile = TouchControls.isTouchDevice()
      ? 'Nifflas - Touch Tutorial.knytt.bin'
      : 'Nifflas - Original Tutorial.knytt.bin';
    const tutorial = this.worlds.find((w) => w.file === tutorialFile);
    const entry =
      this.worlds.find((w) => w.file === lastFile) ?? tutorial ?? this.worlds[0];
    void this.loadBundled(entry);
  }

  private async loadBundled(entry: WorldEntry): Promise<void> {
    this.menu.showLoading(`Loading ${entry.name}…`);
    try {
      const resp = await fetch(`${DATA_BASE}worlds/${encodeURIComponent(entry.file)}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const bytes = new Uint8Array(await resp.arrayBuffer());
      localStorage.setItem('knytt-last-file', entry.file);
      await this.startWorld(bytes, entry.file);
    } catch (e) {
      this.menu.showError(`Failed to load: ${(e as Error).message}`);
    }
  }

  private async loadUploaded(file: File): Promise<void> {
    this.menu.showLoading(`Loading ${file.name}…`);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      await this.startWorld(bytes);
    } catch (e) {
      this.menu.showError(`Failed to load: ${(e as Error).message}`);
    }
  }

  private async startWorld(bytes: Uint8Array, file?: string): Promise<void> {
    // Pixi's renderer was only kicked off in `init()`, not awaited, so the menu
    // could show immediately — wait for it now, alongside the (independent)
    // world parse, since `Game` below needs a ready `this.app`.
    const [world] = await Promise.all([KnyttWorld.load(bytes), this.appReady]);
    const saveKey = `knytt-save:${world.worldName}`;
    // The menu's completed/has-save tint needs file → world-dir (the bin's root dir
    // can differ from the filename, e.g. Original Tutorial's root is "Nifflas - Tutorial").
    if (file) localStorage.setItem(`knytt-dir:${file}`, world.worldName);
    void setLevelIcon(world.getFileBytes('icon.png'));

    let save: KnyttSave;
    const stored = localStorage.getItem(saveKey);
    const newGame = !stored;
    if (stored) {
      save = KnyttSave.parse(stored);
    } else {
      const ds = world.getFileBytes('DefaultSavegame.ini');
      save = ds ? KnyttSave.parse(cp1252.decode(ds)) : new KnyttSave();
    }
    save.worldFolder = world.worldName; // KnyttSave.cs constructor setWorldDirectory

    const game = new Game(this.app, world, save, DATA_BASE);
    game.onSaveWrite = (s) => localStorage.setItem(saveKey, s.toString());
    game.onQuit = () => this.quitToMenu();
    game.onWin = () => this.showWin();
    // StatPanel.cs power names, indexed by PowerNames.
    const POWER_NAMES = ['Run', 'Climb', 'Double Jump', 'High Jump', 'Eye', 'Radar',
      'Umbrella', 'Hologram', 'Red Key', 'Yellow Key', 'Blue Key', 'Purple Key', 'Map'];
    game.onPause = () => {
      const v = game.values;
      new PausePanel(
        this.container,
        world.info.name,
        () => this.game?.setPaused(false),
        () => this.quitToMenu(),
        (muted) => this.game?.audio.setMuted(muted),
        {
          description: world.info.description ?? '',
          powers: POWER_NAMES.filter((_, i) => v.getPower(i)),
          cutscenes: [...v.cutscenes],
          endings: [...v.endings],
        },
      );
    };
    game.onCutscene = (name, onDone, opts) => {
      new Cutscene(this.container, game, name, onDone, opts);
    };
    game.onMap = () => {
      new MapPanel(this.container, game, () => this.game?.setPaused(false));
    };

    this.game = game;
    (window as unknown as { __game: Game }).__game = game;
    game.audio.unlock();
    await game.start();

    // On-screen gamepad bar below the canvas — shown on all viewports (mainly
    // useful on touch devices); its constructor triggers a canvas re-fit.
    this.touch = new TouchControls(game.input, this.container);
    window.dispatchEvent(new Event('resize')); // re-fit the canvas above the bar

    this.menu.hide();

    // New games with an intro cutscene play it before handing over control.
    if (newGame && game.hasCutscene('intro')) {
      game.playCutscene('intro', () => {}, null, 'intro');
    }
  }

  private quitToMenu(): void {
    // yknytt's quit never saves (only SavePoint/Shift/Win write the save) —
    // saving here would be a save-anywhere exploit.
    this.teardownGame();
    this.menu.show();
  }

  private showWin(): void {
    this.teardownGame();
    this.menu.show();
  }

  private teardownGame(): void {
    resetIcon();
    this.touch?.destroy();
    this.touch = null;
    this.game?.destroy();
    this.game = null;
  }
}

const app = new KnyttApp();
void app.init();
